/**
 * MerchantPlatformSync — observability page for the downstream Merchant
 * Platform consumer of ASB606 public APIs.
 *
 * Shows:
 *   - Stat cards (today / 7d / total / last hit)
 *   - Per-endpoint breakdown
 *   - Recent hits log
 *   - Webhook URL config + "Refresh remote cache" button
 *
 * Backend: /api/admin/integrations/merchant-platform/*
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Activity, RefreshCcw, Save, Loader2, Webhook, Globe, Clock,
  CheckCircle2, Send, ExternalLink, BarChart3, Database, AlertCircle,
} from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import DashboardLayout from '../../components/DashboardLayout';
import { useLanguage } from '../../context/LanguageContext';
import { getAuthHeaders } from '../../utils/authHeaders';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BASE = `${API_URL}/api/admin/integrations/merchant-platform`;

const ENDPOINT_LABELS = {
  store_types_active: { ar: 'أنواع المتاجر (المُفعّلة)', en: 'Store Types (active)' },
  store_types_list:   { ar: 'أنواع المتاجر (كاملة)',     en: 'Store Types (full list)' },
  webhook_notify:     { ar: 'Webhook صادر',             en: 'Outbound Webhook' },
};

function fmtRel(iso, isAr) {
  if (!iso) return isAr ? 'لم يحدث بعد' : 'Never';
  const date = new Date(iso);
  const diff = Date.now() - date.getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return isAr ? 'قبل لحظات' : 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return isAr ? `قبل ${min} دقيقة` : `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return isAr ? `قبل ${hr} ساعة` : `${hr} hr ago`;
  const days = Math.floor(hr / 24);
  if (days < 30) return isAr ? `قبل ${days} يوم` : `${days} days ago`;
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtAbs(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

export default function MerchantPlatformSync() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isAr = language === 'ar';

  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [config, setConfig] = useState({ webhook_url: '', updated_at: null, updated_by_name: null });
  const [webhookDraft, setWebhookDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const fetchAll = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [s, l, c] = await Promise.all([
        axios.get(`${BASE}/stats`, { headers: getAuthHeaders() }),
        axios.get(`${BASE}/logs?limit=30`, { headers: getAuthHeaders() }),
        axios.get(`${BASE}/config`, { headers: getAuthHeaders() }),
      ]);
      setStats(s.data || null);
      setLogs(l.data?.data || []);
      setConfig(c.data || {});
      setWebhookDraft(c.data?.webhook_url || '');
    } catch (e) {
      if (!silent) toast.error(isAr ? 'فشل جلب البيانات' : 'Failed to load data');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [isAr]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const refresh = async () => {
    setRefreshing(true);
    await fetchAll(true);
    setRefreshing(false);
    toast.success(isAr ? 'تم التحديث' : 'Refreshed');
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      const body = { webhook_url: webhookDraft.trim() || null };
      await axios.put(`${BASE}/config`, body, { headers: getAuthHeaders() });
      toast.success(isAr ? 'تم حفظ الإعدادات' : 'Settings saved');
      fetchAll(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isAr ? 'فشل الحفظ' : 'Save failed'));
    } finally {
      setSavingConfig(false);
    }
  };

  const notifyRemote = async () => {
    if (!config.webhook_url) {
      toast.error(isAr ? 'لم يتم تكوين Webhook' : 'No webhook configured');
      return;
    }
    setNotifying(true);
    try {
      const res = await axios.post(`${BASE}/notify`, {}, { headers: getAuthHeaders() });
      const ok = res.data?.success;
      if (ok) {
        toast.success(isAr ? 'تم إرسال طلب تحديث الـ Cache بنجاح' : 'Cache invalidation sent successfully');
      } else {
        toast.warning(
          `${isAr ? 'الـ Webhook استجاب بحالة' : 'Webhook responded with status'} ${res.data?.status}`,
        );
      }
      fetchAll(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isAr ? 'فشل الاتصال' : 'Connection failed'));
    } finally {
      setNotifying(false);
    }
  };

  return (
    <DashboardLayout
      title="مزامنة منصة التجار"
      titleEn="Merchant Platform Sync"
      subtitle="مراقبة استهلاك الـ APIs العامة من منصة التجار وإدارة تحديث الـ Cache"
      subtitleEn="Monitor public API consumption by the Merchant Platform and manage cache invalidation"
      screenCode="INT-MP01"
      backPath="/dashboard"
      onBack={() => navigate('/dashboard')}
      breadcrumb={[
        { labelAr: 'لوحة التحكم', labelEn: 'Dashboard', path: '/dashboard' },
        { labelAr: 'التكاملات', labelEn: 'Integrations' },
        { labelAr: 'مزامنة منصة التجار', labelEn: 'Merchant Platform Sync' },
      ]}
    >
      <section
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6"
        data-testid="merchant-sync-page"
      >
        {/* Header actions */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 text-sm">
            <div className="w-9 h-9 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
              <Activity className="h-4 w-4 text-emerald-400" />
            </div>
            <div>
              <p className="text-white font-bold">
                {isAr ? 'حالة المراقبة' : 'Tracking status'}:{' '}
                <span className="text-emerald-400">{isAr ? 'فعّال' : 'Active'}</span>
              </p>
              <p className="text-[10px] text-[#71717A]">
                {isAr ? 'يتم تتبع' : 'Tracking'} {stats?.tracked_paths?.length || 2} {isAr ? 'مسار' : 'endpoints'}
              </p>
            </div>
          </div>
          <Button
            onClick={refresh}
            disabled={refreshing}
            variant="outline"
            className="border-white/10 text-white hover:bg-white/5 gap-2"
            data-testid="refresh-btn"
          >
            {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4" />}
            {isAr ? 'تحديث' : 'Refresh'}
          </Button>
        </div>

        {/* Global stats */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatChip
                icon={Activity}
                color="#10b981"
                label={isAr ? 'اليوم' : 'Today'}
                value={stats?.global?.today ?? 0}
                testid="stat-today"
              />
              <StatChip
                icon={BarChart3}
                color="#3b82f6"
                label={isAr ? 'آخر 7 أيام' : 'Last 7 days'}
                value={stats?.global?.last_7d ?? 0}
                testid="stat-7d"
              />
              <StatChip
                icon={Database}
                color="#a855f7"
                label={isAr ? 'الإجمالي' : 'Total (30d)'}
                value={stats?.global?.total ?? 0}
                testid="stat-total"
              />
              <StatChip
                icon={Clock}
                color="#f59e0b"
                label={isAr ? 'آخر استهلاك' : 'Last hit'}
                value={fmtRel(stats?.global?.last_hit, isAr)}
                testid="stat-last"
                isText
              />
            </div>

            {/* Per-endpoint breakdown */}
            <div className="bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-4">
              <h3 className="text-base font-bold text-white mb-3 flex items-center gap-2">
                <Globe className="h-4 w-4 text-emerald-400" />
                {isAr ? 'تفصيل حسب الـ Endpoint' : 'Per-endpoint breakdown'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {stats?.per_endpoint && Object.entries(stats.per_endpoint).map(([key, m]) => (
                  <div
                    key={key}
                    className="bg-[#0a0a0a] border border-white/10 rounded-xl p-3"
                    data-testid={`endpoint-${key}`}
                  >
                    <p className="text-xs text-[#A1A1AA] font-bold mb-0.5">
                      {ENDPOINT_LABELS[key]?.[isAr ? 'ar' : 'en'] || key}
                    </p>
                    <p className="text-[10px] font-mono text-[#71717A] mb-2 truncate" dir="ltr" title={m.path}>
                      {m.path}
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="text-center">
                        <p className="text-[10px] text-[#71717A]">{isAr ? 'اليوم' : 'Today'}</p>
                        <p className="text-base font-bold text-emerald-400">{m.today}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-[10px] text-[#71717A]">{isAr ? '7 أيام' : '7 days'}</p>
                        <p className="text-base font-bold text-blue-400">{m.last_7d}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-[10px] text-[#71717A]">{isAr ? 'إجمالي' : 'Total'}</p>
                        <p className="text-base font-bold text-purple-400">{m.total}</p>
                      </div>
                    </div>
                    {m.last_hit && (
                      <p className="text-[10px] text-[#71717A] mt-2 pt-2 border-t border-white/5 truncate" title={m.last_origin}>
                        {isAr ? 'آخر استهلاك:' : 'Last:'} {fmtRel(m.last_hit, isAr)}
                        {m.last_origin && (
                          <span className="ms-1 text-emerald-400" dir="ltr">· {m.last_origin}</span>
                        )}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Webhook config */}
            <div className="bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-4">
              <h3 className="text-base font-bold text-white mb-3 flex items-center gap-2">
                <Webhook className="h-4 w-4 text-emerald-400" />
                {isAr ? 'إعدادات Webhook (تحديث Cache منصة التجار)' : 'Webhook (Merchant Platform cache invalidation)'}
              </h3>
              <p className="text-xs text-[#A1A1AA] mb-3 leading-relaxed">
                {isAr
                  ? 'أدخل رابط Webhook الذي تعرضه منصة التجار لاستقبال إشعارات تغيير أنواع المتاجر. عند الضغط على "إجبار تحديث Cache" يتم إرسال POST بـ JSON: {event: "store_types_changed", ts, source}.'
                  : 'Enter the webhook URL exposed by the Merchant Platform to receive store-type change notifications. Clicking "Refresh remote cache" sends a POST with JSON: {event: "store_types_changed", ts, source}.'}
              </p>
              <div className="flex flex-col md:flex-row gap-2">
                <div className="flex-1 relative">
                  <ExternalLink className="h-4 w-4 text-[#71717A] absolute top-1/2 -translate-y-1/2 start-3" />
                  <Input
                    value={webhookDraft}
                    onChange={(e) => setWebhookDraft(e.target.value)}
                    placeholder="https://merchants.your-domain.com/api/webhooks/asb606-refresh"
                    className="bg-white/5 border-white/10 text-white ps-9"
                    dir="ltr"
                    data-testid="webhook-input"
                  />
                </div>
                <Button
                  onClick={saveConfig}
                  disabled={savingConfig || webhookDraft === (config.webhook_url || '')}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                  data-testid="save-webhook-btn"
                >
                  {savingConfig ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  {isAr ? 'حفظ' : 'Save'}
                </Button>
                <Button
                  onClick={notifyRemote}
                  disabled={notifying || !config.webhook_url}
                  className="bg-[#d4af37] hover:bg-[#c9a227] text-black font-bold gap-2"
                  data-testid="notify-btn"
                >
                  {notifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {isAr ? 'إجبار تحديث Cache' : 'Refresh remote cache'}
                </Button>
              </div>
              {config.webhook_url && (
                <p className="text-[10px] text-[#71717A] mt-2 flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3 text-emerald-400" />
                  {isAr ? 'مُحفوظ في' : 'Saved at'} {fmtAbs(config.updated_at)}
                  {config.updated_by_name && (
                    <span className="ms-1">{isAr ? 'بواسطة' : 'by'} {config.updated_by_name}</span>
                  )}
                </p>
              )}
              {!config.webhook_url && (
                <p className="text-[10px] text-amber-400 mt-2 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {isAr ? 'لم يتم تكوين Webhook بعد' : 'No webhook configured yet'}
                </p>
              )}
            </div>

            {/* Recent log */}
            <div className="bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-4">
              <h3 className="text-base font-bold text-white mb-3 flex items-center gap-2">
                <Clock className="h-4 w-4 text-emerald-400" />
                {isAr ? 'آخر 30 استهلاك' : 'Last 30 hits'}
                <span className="text-xs text-[#71717A] font-normal">({logs.length})</span>
              </h3>
              {logs.length === 0 ? (
                <p className="text-xs text-[#71717A] py-6 text-center">
                  {isAr ? 'لا توجد سجلات بعد. سيظهر هنا أي طلب يصل لمسارات أنواع المتاجر.' : 'No hits yet. Any incoming request to store-type endpoints will appear here.'}
                </p>
              ) : (
                <div className="overflow-x-auto" data-testid="logs-table">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-white/10 text-[#71717A]">
                        <th className="text-start py-2 px-2 font-medium">{isAr ? 'الوقت' : 'Time'}</th>
                        <th className="text-start py-2 px-2 font-medium">{isAr ? 'Endpoint' : 'Endpoint'}</th>
                        <th className="text-start py-2 px-2 font-medium">{isAr ? 'الحالة' : 'Status'}</th>
                        <th className="text-start py-2 px-2 font-medium">{isAr ? 'المصدر' : 'Origin'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((row, i) => (
                        <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                          <td className="py-2 px-2 text-[#A1A1AA] whitespace-nowrap" dir="ltr">{fmtAbs(row.ts)}</td>
                          <td className="py-2 px-2 text-white">
                            {row.outbound ? (
                              <span className="inline-flex items-center gap-1 text-amber-400">
                                <Send className="h-3 w-3" />
                                {ENDPOINT_LABELS[row.endpoint]?.[isAr ? 'ar' : 'en'] || row.endpoint}
                              </span>
                            ) : (
                              <span>{ENDPOINT_LABELS[row.endpoint]?.[isAr ? 'ar' : 'en'] || row.endpoint}</span>
                            )}
                          </td>
                          <td className="py-2 px-2">
                            <StatusBadge code={row.status} />
                          </td>
                          <td className="py-2 px-2 text-[#A1A1AA] truncate max-w-[260px]" dir="ltr" title={row.origin}>
                            {row.origin || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </section>
    </DashboardLayout>
  );
}

function StatChip({ icon: Icon, color, label, value, testid, isText }) {
  return (
    <div
      className="bg-white/[0.02] backdrop-blur-xl border rounded-2xl p-3"
      style={{ borderColor: `${color}30` }}
      data-testid={testid}
    >
      <div className="flex items-center gap-2.5">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center border shrink-0"
          style={{ backgroundColor: `${color}15`, borderColor: `${color}40` }}
        >
          <Icon className="h-4 w-4" style={{ color }} />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] text-[#71717A] truncate">{label}</p>
          <p className={`font-bold text-white ${isText ? 'text-xs' : 'text-base'}`}>{value}</p>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ code }) {
  const n = Number(code);
  let color = '#71717A';
  if (n >= 200 && n < 300) color = '#10b981';
  else if (n >= 300 && n < 400) color = '#3b82f6';
  else if (n >= 400 && n < 500) color = '#f59e0b';
  else if (n >= 500) color = '#ef4444';
  return (
    <span
      className="inline-block px-2 py-0.5 rounded-md text-[10px] font-bold border font-mono"
      style={{ color, backgroundColor: `${color}15`, borderColor: `${color}40` }}
      dir="ltr"
    >
      {code}
    </span>
  );
}
